import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertCircle,
  Edit,
  Loader2,
  Package,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import type { ResourceAddonPack, ResourceType } from "../../types/subscription";
import {
  AdminService,
  type CreateAddonPackData,
  type UpdateAddonPackData,
} from "../../services/adminService";

// ─── Form ─────────────────────────────────────────────────────────────────────

type AddonFormProps = {
  pack?: ResourceAddonPack;
  resourceTypes: ResourceType[];
  onSave: (pack: ResourceAddonPack) => void;
  onCancel: () => void;
};

const AddonPackForm: React.FC<AddonFormProps> = ({
  pack,
  resourceTypes,
  onSave,
  onCancel,
}) => {
  const isEditing = !!pack;
  const [formData, setFormData] = useState<CreateAddonPackData>({
    name: pack?.name ?? "",
    description: pack?.description ?? "",
    resource_type_id: pack?.resource_type_id ?? (resourceTypes[0]?.id ?? ""),
    quantity: pack?.quantity ?? 1,
    price: pack?.price ?? undefined,
    is_active: pack?.is_active ?? true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof CreateAddonPackData, value: unknown) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError("Name is required"); return; }
    if (!formData.resource_type_id) { setError("Resource type is required"); return; }
    if (formData.quantity < 1) { setError("Quantity must be at least 1"); return; }

    setIsSubmitting(true);
    setError(null);

    try {
      let result;
      if (isEditing && pack) {
        const updateData: UpdateAddonPackData = { id: pack.id, ...formData };
        result = await AdminService.updateAddonPack(updateData);
      } else {
        result = await AdminService.createAddonPack(formData);
      }
      if (result.error) { setError(result.error); return; }
      if (result.data) onSave(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="addon-name">Pack Name *</Label>
          <Input
            id="addon-name"
            value={formData.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Extra 5 Policies"
            required
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="addon-desc">Description</Label>
          <Textarea
            id="addon-desc"
            value={formData.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Brief description"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="addon-resource">Resource Type * <span className="text-xs text-muted-foreground">(count type only)</span></Label>
          <select
            id="addon-resource"
            value={formData.resource_type_id}
            onChange={(e) => set("resource_type_id", e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            required
          >
            <option value="">Select resource type</option>
            {resourceTypes
              .filter((rt) => !rt.value_type || rt.value_type === "count")
              .map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name} ({rt.code})
                </option>
              ))}
          </select>
        </div>

        <div>
          <Label htmlFor="addon-qty">Quantity (units granted) *</Label>
          <Input
            id="addon-qty"
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => set("quantity", parseInt(e.target.value) || 1)}
            required
          />
        </div>

        <div>
          <Label htmlFor="addon-price">Price ($)</Label>
          <Input
            id="addon-price"
            type="number"
            min="0"
            step="0.01"
            value={formData.price ?? ""}
            onChange={(e) =>
              set("price", e.target.value === "" ? undefined : parseFloat(e.target.value))
            }
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground mt-1">Leave empty for free</p>
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <Label htmlFor="addon-active" className="cursor-pointer">
            Active
            <span className="block text-xs text-muted-foreground">
              Visible to users
            </span>
          </Label>
          <Switch
            id="addon-active"
            checked={formData.is_active}
            onCheckedChange={(v) => set("is_active", v)}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={isSubmitting} className="flex-1 sm:flex-none">
          {isSubmitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEditing ? "Updating..." : "Creating..."}</>
          ) : (
            <><Save className="mr-2 h-4 w-4" />{isEditing ? "Update Pack" : "Create Pack"}</>
          )}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          <X className="mr-2 h-4 w-4" />Cancel
        </Button>
      </div>
    </form>
  );
};

// ─── Manager ──────────────────────────────────────────────────────────────────

export const AddonPacksManager: React.FC = () => {
  const { toast } = useToast();
  const [packs, setPacks] = useState<ResourceAddonPack[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPack, setEditingPack] = useState<ResourceAddonPack | null>(null);

  const load = async () => {
    setIsLoading(true);
    const [packsRes, rtRes] = await Promise.all([
      AdminService.getAddonPacks(),
      AdminService.getResourceTypes(),
    ]);
    setPacks(packsRes.data);
    setResourceTypes(rtRes.data);
    setIsLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = (pack: ResourceAddonPack) => {
    setPacks((prev) =>
      prev.some((p) => p.id === pack.id)
        ? prev.map((p) => (p.id === pack.id ? pack : p))
        : [pack, ...prev]
    );
    setShowForm(false);
    setEditingPack(null);
    toast({ title: editingPack ? "Addon pack updated" : "Addon pack created" });
  };

  const handleDelete = async (id: string) => {
    const { error } = await AdminService.deleteAddonPack(id);
    if (error) {
      toast({ title: "Failed to delete addon pack", variant: "destructive" });
      return;
    }
    setPacks((prev) => prev.filter((p) => p.id !== id));
    toast({ title: "Addon pack deactivated" });
  };

  const openEdit = (pack: ResourceAddonPack) => {
    setEditingPack(pack);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPack(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Addon Packs</h2>
          <p className="text-sm text-muted-foreground">
            Purchasable quota packs users can buy to extend their plan limits.
            Each pack grants extra units for a specific resource type.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Addon Pack
        </Button>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPack ? "Edit Addon Pack" : "Create Addon Pack"}
            </DialogTitle>
          </DialogHeader>
          <AddonPackForm
            pack={editingPack ?? undefined}
            resourceTypes={resourceTypes}
            onSave={handleSave}
            onCancel={closeForm}
          />
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : packs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Package className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No addon packs yet.</p>
            <Button variant="outline" className="mt-4" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />Create First Pack
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {packs.map((pack) => {
            const rt = pack.resource_type ?? resourceTypes.find((r) => r.id === pack.resource_type_id);
            return (
              <Card key={pack.id} className={!pack.is_active ? "opacity-60" : ""}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-start justify-between gap-2 text-base">
                    <span>{pack.name}</span>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(pack)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        onClick={() => handleDelete(pack.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {pack.description && (
                    <p className="text-muted-foreground text-xs">{pack.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Resource</span>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      {rt?.code ?? pack.resource_type_id.slice(0, 8)}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Grants</span>
                    <Badge variant="secondary">
                      +{pack.quantity} {rt?.unit_label ?? "units"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Price</span>
                    <span className="font-medium">
                      {pack.price != null ? `$${pack.price}` : "Free"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant={pack.is_active ? "default" : "outline"}>
                      {pack.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
